import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllMentorsApi } from "../services/mentorProfile";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";
import {
  selectMentors, selectMentorsLoading, selectMentorsCacheValid,
  setMentors, setMentorsLoading, setMentorsError
} from "../redux/mentorSlice";

const aggregateSkills = (m) => [
  ...(m?.AILiteracySkills || []), ...(m?.appliedAISkills || []), ...(m?.ethicsResponsibleAI || []),
  ...(m?.softAISkills || []), ...(m?.strategicAISkills || []), ...(m?.technicalAISkills || []),
  ...(m?.TechnicalDevelopmentRoles || []), ...(m?.InfrastureRoles || []), ...(m?.ethicsRoles || []),
  ...(m?.buisnessRole || []), ...(m?.CreativeRole || []), ...(m?.Compliance || []),
  ...(m?.CommunicationSkills || []), ...(m?.InterpersonalTeamworkSkills || []),
  ...(m?.CriticalThinkingProblemSolving || []), ...(m?.SelfManagementSkills || []),
  ...(m?.LeadershipInfluence || [])
];

const aggregateTools = (m) => [
  ...(m?.MachineLearningDeepLearningPlatforms || []), ...(m?.AutoMLNoCodeLowCodeAIPlatforms || []),
  ...(m?.NaturalLanguageProcessingTools || []), ...(m?.ComputerVisionTools || []),
  ...(m?.GenerativeAITools || []), ...(m?.MLOpsDeploymentTools || []),
  ...(m?.DataScienceVisualizationTools || [])
];

export const useMentors = () => {
  const navigate = useNavigate(), dispatch = useDispatch();
  const mentors = useSelector(selectMentors);
  const mentorsLoading = useSelector(selectMentorsLoading);
  const isCacheValid = useSelector(selectMentorsCacheValid);

  const [filteredMentors, setFilteredMentors] = useState([]);
  const [isMentorsReady, setIsMentorsReady] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ occupation: "", aiCareer: "", aiSkill: "", aiTool: "" });

  const getMentorReview = useCallback(async (id) => {
    try {
      const { data } = await api1.get(`${API_ENDPOINTS.GET_MENTOR_REVIEW}/${id}`);
      return data || { totalReviews: 0 };
    } catch {
      return { totalReviews: 0 };
    }
  }, []);

  const handleAppointment = (userId) => navigate(`/dashboard/book-appointment/${userId}`);

  const handleFilterSelect = (filter) => {
    const sortMap = {
      "Reviews": (a, b) => b.reviews.totalReviews - a.reviews.totalReviews,
      "Years of Experience": (a, b) => (b.yearsOfExperience || 0) - (a.yearsOfExperience || 0),
      "Lowest Price": (a, b) => (a.mentorMeeting?.["60minmeeting"] || Infinity) -
                                (b.mentorMeeting?.["60minmeeting"] || Infinity)
    };
    setFilteredMentors([...filteredMentors].sort(sortMap[filter] || (() => 0)));
  };

  const handleSearch = (term) => {
    const t = term.toLowerCase();
    setFilteredMentors(mentors.filter(m =>
      `${m?.firstName} ${m?.lastName} ${m?.certificationName || ""} ${m?.state || ""} ${m?.country || ""} ${m?.timezone || ""}`
        .toLowerCase().includes(t)
    ));
  };

  const handleCategoryFilter = (type, val) => {
    const newFilters = { ...activeFilters, [type]: val };
    setActiveFilters(newFilters);

    let result = mentors;
    if (newFilters.occupation)
      result = result.filter(m => m?.currentJobOccupation?.toLowerCase().includes(newFilters.occupation.toLowerCase()));
    if (newFilters.aiCareer)
      result = result.filter(m => m?.EmergingAI?.some(c => c?.toLowerCase().includes(newFilters.aiCareer.toLowerCase())));
    if (newFilters.aiSkill)
      result = result.filter(m => aggregateSkills(m).some(s => s?.toLowerCase().includes(newFilters.aiSkill.toLowerCase())));
    if (newFilters.aiTool)
      result = result.filter(m => aggregateTools(m).some(t => t?.toLowerCase().includes(newFilters.aiTool.toLowerCase())));

    setFilteredMentors(result);
  };

  const clearFilters = () => {
    setActiveFilters({ occupation: "", aiCareer: "", aiSkill: "", aiTool: "" });
    setFilteredMentors(mentors);
  };

  const refreshMentors = () => { dispatch(setMentorsLoading(true)); setIsMentorsReady(false); dispatch(setMentors([])); };

  useEffect(() => {
    const fetchMentors = async () => {
      if (!mentors.length || !isCacheValid) {
        dispatch(setMentorsLoading(true));
        try {
          const allMentors = await getAllMentorsApi();
          const mentorsWithReviews = await Promise.all(allMentors.map(async m => ({
            ...m, reviews: await getMentorReview(m.userId)
          })));
          dispatch(setMentors(mentorsWithReviews));
          setFilteredMentors(mentorsWithReviews);
        } catch (err) {
          dispatch(setMentorsError(err.message));
        }
      } else setFilteredMentors(mentors);
      setIsMentorsReady(true);
    };
    fetchMentors();
  }, [dispatch, mentors.length, isCacheValid, getMentorReview]);

  return {
    mentors, filteredMentors, mentorsLoading, isMentorsReady, activeFilters,
    handleAppointment, handleFilterSelect, handleSearch, handleCategoryFilter,
    clearFilters, refreshMentors, isLoading: mentorsLoading || !isMentorsReady
  };
};
